// Display helpers shared by the hotel pages.
//
// The flight pages each carry their own copy of fmtMoney/fmtDate — that made
// sense when there were three of them and they diverged. The hotel flow has
// eight, and they need to agree on things the flight flow never had to express
// (an unrated property, a "taxes at property" line, a cancellation deadline),
// so those live here once.

import type { BoardBasis, HotelCard, Occupancy } from "./hotelTypes";

export function fmtMoney(n: number | null | undefined, currency: string): string {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency, maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString("en-IN")}`;
  }
}

/** "15 Oct" */
export function fmtDate(ymd?: string | null): string {
  if (!ymd) return "—";
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(ymd);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

/** "Thu, 15 Oct" */
export function fmtDateLong(ymd?: string | null): string {
  if (!ymd) return "";
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(ymd);
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

/** "Thu, 15 Oct 2026" */
export function fmtDateFull(ymd?: string | null): string {
  if (!ymd) return "";
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(ymd);
  return d.toLocaleDateString("en-IN", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

/** A cancellation deadline, which arrives as a full timestamp rather than a date. */
export function fmtDeadline(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return (
    d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) +
    ", " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}

export function nightsLabel(nights: number): string {
  return `${nights} ${nights === 1 ? "night" : "nights"}`;
}

/** "2 rooms · 4 adults · 1 child" — the room count first, since it's what
 *  decides how many guest names get collected. */
export function occupancyLabel(occupancies: Occupancy[]): string {
  const rooms = occupancies.length;
  const adults = occupancies.reduce((n, o) => n + (o.adults || 0), 0);
  const children = occupancies.reduce((n, o) => n + (o.children?.length || 0), 0);
  const parts = [`${rooms} ${rooms === 1 ? "room" : "rooms"}`,
                 `${adults} ${adults === 1 ? "adult" : "adults"}`];
  if (children) parts.push(`${children} ${children === 1 ? "child" : "children"}`);
  return parts.join(" · ");
}

const BOARD_LABEL: Record<string, string> = {
  room_only: "Room only",
  bed_and_breakfast: "Breakfast included",
  half_board: "Half board",
  full_board: "Full board",
  all_inclusive: "All inclusive",
};

export function fmtBoard(basis: BoardBasis | string | null | undefined, fallback?: string): string {
  if (!basis) return fallback || "Room only";
  return BOARD_LABEL[basis] || fallback || String(basis).replace(/_/g, " ");
}

/**
 * The review score as the design renders it, or null when there ISN'T one.
 *
 * The provider uses 0 as a "no score" sentinel — five-star properties come back
 * with `rating: 0` and thirteen thousand reviews — so an absent score has to
 * render as "Unrated", never as 0.0. The backend already normalises the
 * sentinel to null; this is the guard against rendering that null as a number.
 */
export function reviewLabel(hotel: Pick<HotelCard, "review_rating" | "review_count">):
  { score: string; word: string; count: string } | null {
  const rating = hotel.review_rating;
  if (!rating) return null;
  const word =
    rating >= 4.5 ? "Exceptional" :
    rating >= 4.2 ? "Excellent" :
    rating >= 3.8 ? "Very good" :
    rating >= 3.2 ? "Good" : "Fair";
  const count = hotel.review_count
    ? `${new Intl.NumberFormat("en-IN").format(hotel.review_count)} reviews`
    : "";
  return { score: rating.toFixed(1), word, count };
}

/** "★★★★☆" for a rated property, or null. Same sentinel rule as reviews. */
export function starLabel(stars: number | null | undefined): string | null {
  if (!stars || stars < 1) return null;
  return "★".repeat(Math.min(stars, 5));
}

/**
 * A supplier's tax description, made readable.
 *
 * These arrive as raw enum-ish strings — "mandatory_tax", "city_tax" — mixed in
 * with real prose like "A tax is imposed by the city". Underscores become
 * spaces and the first letter is capitalised; nothing else is touched, so real
 * sentences pass through unchanged and no meaning is invented.
 */
export function fmtTaxDescription(value: string | null | undefined): string {
  const text = (value || "").trim();
  if (!text) return "Tax collected by the property";
  const spaced = text.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function ymdToDate(value: string): Date {
  return new Date(`${value}T00:00:00`);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function nightsBetween(checkIn: string, checkOut: string): number {
  const a = ymdToDate(checkIn).getTime();
  const b = ymdToDate(checkOut).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(Math.round((b - a) / 86_400_000), 0);
}
